import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { SystemMessageType } from "@psei/ng/api";
import { isArray } from "lodash";
import { AccountIalEnum } from "../../models/account.model";

export const MEGABYTE_MULTIPLIER = 1024 * 1024; // Megabyte

const isEmptyInputValue = (value: any): boolean => {
  if (value) {
    return value === null || value.length === 0;
  }
  return true;
};

const stringRegexChecker = (
  allowed: RegExp,
  disallowed: RegExp,
  customError: string,
  customType: SystemMessageType
) => {
  return (control: AbstractControl): ValidationErrors => {
    const value = control.value;
    if (isEmptyInputValue(value)) {
      return null;
    }
    const isRegExpValid = allowed.test(value) && !disallowed.test(value);
    const result = !isRegExpValid
      ? {
          string: true,
          message: customError,
          type: customType || SystemMessageType.Negative,
        }
      : null;
    return result;
  };
};

export const getErrorFilesByType = (
  files: File[],
  fileType: string = "application/pdf"
): File[] => {
  const errorFiles: File[] = [];

  if (files.length > 0) {
    files.forEach((file: File) => {
      if (file.type !== fileType) {
        errorFiles.push(file);
      }
    });
  }
  return errorFiles;
};

export const getErrorFilesByMaxSize = (
  files: File[],
  maxFileSizeInMegabytes: number = 20
): File[] => {
  const errorFiles: File[] = [];

  if (files.length > 0) {
    files.forEach((file: File) => {
      if (file.size / MEGABYTE_MULTIPLIER > maxFileSizeInMegabytes) {
        errorFiles.push(file);
      }
    });
  }
  return errorFiles;
};

export const getErrorFilesByMinSize = (
  files: File[],
  minFileSizeInMegabytes: number = 0
): File[] => {
  const errorFiles: File[] = [];

  if (files.length > 0) {
    files.forEach((file: File) => {
      if (file.size / MEGABYTE_MULTIPLIER <= minFileSizeInMegabytes) {
        errorFiles.push(file);
      }
    });
  }
  return errorFiles;
};

export class CustomValidators {
  static minMaxIal(
    initialValue: AccountIalEnum,
    adminIal: AccountIalEnum,
    ialArr: AccountIalEnum[],
    customError: string = null,
    customType: SystemMessageType = SystemMessageType.Negative
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const ial = control.value;

      if (isEmptyInputValue(ial) || ial === initialValue) {
        return null;
      }
      const isLoweringIal = ialArr.indexOf(ial) < ialArr.indexOf(initialValue);
      const isChangePermittedByAdminIal =
        ialArr.indexOf(ial) > ialArr.indexOf(adminIal);

      const isValid = !isLoweringIal && !isChangePermittedByAdminIal;

      const message = isLoweringIal
        ? $localize`:@@custom-validator.min_max_ial:Nie posiadasz możliwości obniżenia poziomu sprawdzenia tożsamości`
        : isChangePermittedByAdminIal
        ? $localize`:@@custom-validator.change_permitted_by_admin_ial:Nie posiadasz możliwości zmiany poziomu sprawdzenia tożsamości na poziom wyższy niż własny`
        : $localize`:@@custom-validator.change_not_permitted_by_admin_ial:Niepoprawny poziom sprawdzenia tożsamości`;

      return !isValid
        ? {
            ial: true,
            message: customError || message,
            type: customType || SystemMessageType.Negative,
          }
        : null;
    };
  }

  static simplePhone(
    customError: string = null,
    customType: SystemMessageType = SystemMessageType.Negative
  ): ValidatorFn {
    const regExp = /^(\+)?[0-9]{9,15}$/;
    return (control: AbstractControl): ValidationErrors => {
      const phone = control.value;

      if (isEmptyInputValue(phone)) {
        return null;
      }

      const isValid = regExp.test(phone);
      return !isValid
        ? {
            phone: true,
            message:
              customError ||
              $localize`:@@custom-validator.phone:Nieprawidłowy numer telefonu`,
            type: customType || SystemMessageType.Negative,
          }
        : null;
    };
  }

  /**
   * Validate is phone number is correct, prefix is allowed, and must be +48 or 0048
   * @param customError Custom error message
   * @param customType Custom PSEI SystemMessage type
   * @param minLength minimal phone number length
   * @param maxLength maximal phone number length
   */
  static phone(
    customError: string = null,
    customType: SystemMessageType = SystemMessageType.Negative,
    minLength: number = 9,
    maxLength: number = 9
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const phoneNumber = control.value;
      if (isEmptyInputValue(phoneNumber)) {
        return null;
      }

      const isNumbersOnlyRegex = /^[0-9]+$/;
      const isValid =
        isNumbersOnlyRegex.test(phoneNumber) &&
        phoneNumber.length >= minLength &&
        phoneNumber.length <= maxLength;
      const result = !isValid
        ? {
            phone: true,
            message:
              customError ||
              $localize`:@@custom-validator.phone:Nieprawidłowy numer telefonu`,
            type: customType || SystemMessageType.Negative,
          }
        : null;
      return result;
    };
  }

  /**
   * Validator that requires a control to be a validate NIP number
   *
   * @param customError Custom error message
   * @param customType Custom PSEI SystemMessage type
   */
  static nip(
    customError: string = null,
    customType: SystemMessageType = SystemMessageType.Negative
  ): ValidatorFn {
    const regExp = /^[0-9]*$/;
    const weight = [6, 5, 7, 2, 3, 4, 5, 6, 7];
    return (control: AbstractControl): ValidationErrors => {
      const nip = control.value;

      if (isEmptyInputValue(nip)) {
        return null;
      }

      const isRegExpValid = regExp.test(nip);

      if (!isRegExpValid) {
        return {
          nip: true,
          message:
            customError ||
            $localize`:@@custom-validator.nip:Dozwolone tylko cyfry`,
          type: customType || SystemMessageType.Negative,
        };
      }

      if (nip.length !== 10) {
        return {
          nip: true,
          message:
            customError ||
            $localize`:@@custom-validator.nip:NIP powinien składać się z 10 cyfr`,
          type: customType || SystemMessageType.Negative,
        };
      }

      const nipArr = [...nip].map((num) => Number(num));
      const res = nipArr.reduce(
        (acc, curr, i) => (i < 9 ? (acc += weight[i] * curr) : acc),
        0
      );
      const isValid = res % 11 === nipArr[9];
      return !isValid
        ? {
            nip: true,
            message:
              customError ||
              $localize`:@@custom-validator.nip:Nieprawidłowy numer NIP`,
            type: customType || SystemMessageType.Negative,
          }
        : null;
    };
  }

  /**
   * Validator that requires a control to be a validate REGON number
   *
   * @param customError Custom error message
   * @param customType Custom PSEI SystemMessage type
   */
  static regon(
    customError: string = null,
    customType: SystemMessageType = SystemMessageType.Negative
  ): ValidatorFn {
    const regExp = /^([0-9]{9}|[0-9]{14})$/;
    const weight9 = [8, 9, 2, 3, 4, 5, 6, 7];
    const weight14 = [2, 4, 8, 5, 0, 9, 7, 3, 6, 1, 2, 4, 8];
    return (control: AbstractControl): ValidationErrors => {
      const regon = control.value;
      if (isEmptyInputValue(regon)) {
        return null;
      }
      const regonWithoutDashes = regon.replace(/-/g, "");
      const isRegExpValid = regExp.test(regonWithoutDashes);
      const regonLength = regonWithoutDashes.length;
      const regonArr = [...regonWithoutDashes].map((num) => Number(num));
      const res = regonArr.reduce((acc, curr, i) => {
        if (regonLength === 9 && i < 8) {
          return (acc += weight9[i] * curr);
        } else if (regonLength === 14 && i < 13) {
          return (acc += weight14[i] * curr);
        }
        return acc;
      }, 0);
      const isValid =
        +[...(res % 11).toString()].pop() ===
        (regonLength === 9 ? regonArr[8] : regonArr[13]);
      const result =
        !isValid || !isRegExpValid
          ? {
              regon: true,
              message:
                customError ||
                $localize`:@@custom-validator.regon:Nieprawidłowy numer REGON`,
              type: customType || SystemMessageType.Negative,
            }
          : null;
      return result;
    };
  }

  /**
   * Validator that requires a control to be a validate KRS number
   *
   * @param customError Custom error message
   * @param customType Custom PSEI SystemMessage type
   */
  static krs(
    customError: string = null,
    customType: SystemMessageType = SystemMessageType.Negative
  ): ValidatorFn {
    const regExp = /^\d{10}$/;
    return (control: AbstractControl): ValidationErrors => {
      const krs = control.value;
      if (isEmptyInputValue(krs)) {
        return null;
      }
      const isRegExpValid = regExp.test(krs);
      const result = !isRegExpValid
        ? {
            krs: true,
            message:
              customError ||
              $localize`:@@custom-validator.krs:Nieprawidłowy numer KRS`,
            type: customType || SystemMessageType.Negative,
          }
        : null;
      return result;
    };
  }

  /**
   * Validator that requires a control to be a validate EIC number
   *
   * @param customError Custom error message
   * @param customType Custom PSEI SystemMessage type
   */
  static eic(
    customError: string = null,
    customType: SystemMessageType = SystemMessageType.Negative
  ): ValidatorFn {
    const regExp = /^[A-Z0-9]{3}[A-Z0-9-]{12}[A-Z0-9]{1}$/;
    return (control: AbstractControl): ValidationErrors => {
      const eic = control.value;
      if (isEmptyInputValue(eic)) {
        return null;
      }
      const isRegExpValid = regExp.test(eic);
      const result = !isRegExpValid
        ? {
            eic: true,
            message:
              customError ||
              $localize`:@@custom-validator.eic:Nieprawidłowy numer EIC, dopuszczalne wielkie litery, cyfry oraz znak "-"`,
            type: customType || SystemMessageType.Negative,
          }
        : null;
      return result;
    };
  }

  /**
   * Validator that requires a control to be a validate PESEL number
   *
   * @param customError Custom error message
   * @param customType Custom PSEI SystemMessage type
   */
  static pesel(
    customError: string = null,
    customType: SystemMessageType = SystemMessageType.Negative
  ): ValidatorFn {
    const regExp = /^[0-9]{11}$/;
    const weight = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
    return (control: AbstractControl): ValidationErrors => {
      const pesel = control.value;
      if (isEmptyInputValue(pesel)) {
        return null;
      }
      const isRegExpValid = regExp.test(pesel);
      const peselArr = [...pesel].map((num) => Number(num));
      const peselLength = peselArr.length;
      const res = peselArr.reduce(
        (acc, curr, i) => (i < 10 ? (acc += (weight[i] * curr) % 10) : acc),
        0
      );
      const isValid = 10 - (res % 10) === peselArr[peselLength - 1];
      const result =
        !isValid || !isRegExpValid
          ? {
              pesel: true,
              message:
                customError ||
                $localize`:@@custom-validator.pesel:Nieprawidłowy numer PESEL`,
              type: customType || SystemMessageType.Negative,
            }
          : null;
      return result;
    };
  }

  /**
   * Validator that requires a control to be a validate email with required TLD and SLD
   *
   * Why not angular Validators.email ?
   * According to the RFC, email address with TLDs only, is valid, e.g. me @ localhost, me@127.0.0.1 me @ domain
   *
   * @param customError Custom error message
   * @param customType Custom PSEI SystemMessage type
   */
  static email(
    customError: string = null,
    customType: SystemMessageType = SystemMessageType.Negative
  ): ValidatorFn {
    // tslint:disable-next-line: max-line-length
    const regExp =
      /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z](?:[a-zA-Z-]{0,61}[a-zA-Z]))+$/;
    return (control: AbstractControl): ValidationErrors => {
      const email = control.value;
      if (isEmptyInputValue(email)) {
        return null;
      }
      const isRegExpValid = regExp.test(email);
      const result = !isRegExpValid
        ? {
            email: true,
            message:
              customError ||
              $localize`:@@custom-validator.email:Nieprawidłowy numer e-mail`,
            type: customType || SystemMessageType.Negative,
          }
        : null;
      return result;
    };
  }

  /**
   * Validator that requires a control to be a validate length based on minLength argument
   *
   * @param minLength Min length of array (default value 1)
   * @param customError Custom error message
   * @param customType Custom PSEI SystemMessage type
   */
  static maxLength(
    maxLength: number = 1,
    customError: string = null,
    customType: SystemMessageType = SystemMessageType.Negative
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const inputValue = control.value;
      if (isEmptyInputValue(inputValue)) {
        return null;
      }
      const isRegExpValid = inputValue && inputValue.length <= maxLength;
      const result = !isRegExpValid
        ? {
            maxLength: true,
            message:
              customError ||
              $localize`:@@custom-validator.max_length:Maksymalna liczba znaków to ` +
                maxLength,
            type: customType || SystemMessageType.Negative,
          }
        : null;
      return result;
    };
  }

  static minLength(
    minLength: number = 1,
    customError: string = null,
    customType: SystemMessageType = SystemMessageType.Negative
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const inputValue = control.value;
      if (isEmptyInputValue(inputValue)) {
        return null;
      }
      const isRegExpValid = inputValue && inputValue.length >= minLength;
      const result = !isRegExpValid
        ? {
            maxLength: true,
            message:
              customError ||
              $localize`:@@custom-validator.min_length:Minimalna liczba znaków to ` +
                minLength,
            type: customType || SystemMessageType.Negative,
          }
        : null;
      return result;
    };
  }

  /**
   * Validator that requires a control to be a validate array length based on minLength argument
   *
   * @param minLength Min length of array (default value 1)
   * @param customError Custom error message
   * @param customType Custom PSEI SystemMessage type
   */
  static arrayMinLength(
    minLength: number = 1,
    customError: string = null,
    customType: SystemMessageType = SystemMessageType.Negative
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const arr = control.value;
      const isRegExpValid = isArray(arr) && arr.length >= minLength;
      const result = !isRegExpValid
        ? {
            arrayMinLength: true,
            message:
              customError ||
              $localize`:@@custom-validator.array_min_length:Nieprawidłowa liczba w tablicy`,
            type: customType || SystemMessageType.Negative,
          }
        : null;
      return result;
    };
  }

  /**
   * Validator that requires a control to be a valid firstName
   *
   * @param customError Custom error message
   * @param customType Custom PSEI SystemMessage type
   */
  static firstName(
    customError: string = $localize`:@@custom-validator.name:Użyte niepoprawne znaki`,
    customType: SystemMessageType = SystemMessageType.Negative
  ): ValidatorFn {
    const allowed = /[\p{L} ]+/u;
    const disallowed = /[-1234567890~`!@#$%^&*()_+=<>,.:;"'\{\}\[\]]+/u;
    return stringRegexChecker(allowed, disallowed, customError, customType);
  }

  /**
   * Validator that requires a control to use only latin characters
   *
   * @param customError Custom error message
   * @param customType Custom PSEI SystemMessage type
   */
  static latinCharactersOnly(
    customError: string = $localize`:@@custom-validator.name:Użyte niepoprawne znaki`,
    customType: SystemMessageType = SystemMessageType.Negative
  ): ValidatorFn {
    const allowed = /^[a-z]+$/i;
    const disallowed = /[-1234567890~`!@#$%^&*()_+=<>,.:;"'\{\}\[\]]+/u;
    return stringRegexChecker(allowed, disallowed, customError, customType);
  }

  /**
   * Validator that requires a control to use only numbers or letters without characters
   *
   * @param customError Custom error message
   * @param customType Custom PSEI SystemMessage type
   */
  static noSpecialCharacters(
    customError: string = $localize`:@@custom-validator.name:Użyte niepoprawne znaki`,
    customType: SystemMessageType = SystemMessageType.Negative
  ): ValidatorFn {
    const allowed = /[\p{L}0-9]+/iu;
    const disallowed = /[-|~`!@#$%^&*()_+=<>,.:;"\\//'\{\}\[\]]+/u;
    return stringRegexChecker(allowed, disallowed, customError, customType);
  }

  /**
   * Validator that requires a control to be a valid lastName
   *
   * @param customError Custom error message
   * @param customType Custom PSEI SystemMessage type
   */
  static lastName(
    customError: string = $localize`:@@custom-validator.name:Użyte niepoprawne znaki`,
    customType: SystemMessageType = SystemMessageType.Negative
  ): ValidatorFn {
    const allowed = /[\p{L} -]+/u;
    const disallowed = /[1234567890~`!@#$%^&*()_+=<>,.:;"'\{\}\[\]]+/u;
    return stringRegexChecker(allowed, disallowed, customError, customType);
  }

  static maxFileSize(
    maxFileSizeInMegabytes: number = 20,
    customError: string = $localize`:@@custom-validator.max_file_size:Zbyt duży plik`
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      let errorFiles: File[] = null;
      if (control.value && control.value.length > 0) {
        errorFiles = getErrorFilesByMaxSize(
          control.value,
          maxFileSizeInMegabytes
        );
      }
      return errorFiles && errorFiles.length > 0
        ? {
            message:
              customError +
              " " +
              errorFiles.map((errorFile) => errorFile.name).join(", "),
          }
        : null;
    };
  }

  static minFileSize(
    minFileSizeInMegabytes: number = 0,
    customError: string = $localize`:@@custom-validator.min_file_size:Zbyt mały rozmiar pliku`
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      let errorFiles: File[] = null;
      if (control.value && control.value.length > 0) {
        errorFiles = getErrorFilesByMinSize(
          control.value,
          minFileSizeInMegabytes
        );
      }

      return errorFiles && errorFiles.length > 0
        ? {
            message:
              customError +
              " " +
              errorFiles.map((errorFile) => errorFile.name).join(", "),
          }
        : null;
    };
  }

  static fileType(
    fileType: string = "application/pdf",
    customError: string = $localize`:@@custom-validator.file_type:Niepoprawny typ pliku`
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      let errorFiles = null;
      if (control.value && control.value.length > 0) {
        errorFiles = getErrorFilesByType(control.value, fileType);
      }
      return (!!errorFiles && errorFiles.length) > 0
        ? {
            message:
              customError +
              " " +
              errorFiles.map((errorFile) => errorFile.name).join(", "),
          }
        : null;
    };
  }

  static maxFileCount(
    maxFileCount: number = 5,
    customError: string = $localize`:@@custom-validator.max_file_count:Zbyt wiele plików dodanych`
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      return control.value && control.value.length > maxFileCount
        ? {
            message: customError,
          }
        : null;
    };
  }

  static trimSpacesValidator(
    customError: string = $localize`:@@custom-validator.trim_spaces_validator:Niedozwolone spacje`
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      return control.value &&
        typeof control.value === "string" &&
        control.value.trim() !== control.value
        ? {
            message: customError,
          }
        : null;
    };
  }
}
